import ContractualAccountDTO from 'features/contracts/dtos/ContractualAccountDTO';
import CommodityIconList from 'features/shared/components/icons/CommodityIconList';
import AddressDTO from 'features/shared/dtos/AddressDTO';
import FormGroup from 'features/shared/forms/components/FormGroup';
import { getAddressSections } from 'features/shared/helpers/AddressFormatter';
import React from 'react';
import Select, { components, createFilter, OptionsType, MultiValueProps, SingleValueProps } from "react-select";
import { Config } from 'react-select/src/filters';
import styles from './contractualAccountSelect.module.scss';
import reactSelectStyles from 'features/shared/components/reactSelect/reactselectcustom.module.scss';
import { Nullable } from 'features/shared/types/nullable';
import { isEmpty } from 'features/shared/helpers/objects';
import { useTranslation } from 'react-i18next';

interface Props {
  name: string;
  contractualAccounts: ContractualAccountDTO[];
  onChange?: (contract: Nullable<ContractualAccountDTO>) => void;
  onChangeMulti?: (contractulAccountIdsCsv: string) => void;
  selectedContractualAccount?: Nullable<ContractualAccountDTO>;
  isMulti?: boolean;
  errors?: Record<string, string>;
  label?: string;
  placeholder?: string;
}

const ContractualAccountSelect = ({
  name,
  contractualAccounts,
  onChange,
  onChangeMulti,
  selectedContractualAccount,
  errors,
  isMulti = false,
  label,
  placeholder
}: Props) => {
  // translations
  const tLabels = useTranslation("contracts\\contracts").t;

  // helpers
  const getInlineAddress = (address: AddressDTO): string => {
    let sections = getAddressSections(address);    
    return `${sections.streetSection}, ${sections.citySection}`;
  }

  const getPlaceholderText = () => { 
    if (placeholder) {
      return placeholder;
    } else if (isMulti) {
      return tLabels("contractualAccountSelect.selectContractualAccounts");
    } else {
      return tLabels("contractualAccountSelect.selectContractualAccount");
    }
  }

  // filter setting
  const filterConfig: Config = {
    stringify: (obj: any) => {
      const { number, address } = obj.data as ContractualAccountDTO;
      const inlineAddress = getInlineAddress(address!);
      return `${number} ${inlineAddress}`;
    }
  };

  // handlers
  const selectChangeHandler = (accounts: Nullable<ContractualAccountDTO | OptionsType<ContractualAccountDTO>>) => {
    if (isMulti && onChangeMulti) {
      const accountIds = (accounts as OptionsType<ContractualAccountDTO>).map((account) => account.contractualAccountId!);
      onChangeMulti(accountIds.join(","));
    } else if (onChange) {
      onChange(accounts as ContractualAccountDTO);
    }
  }

  // sub-renders
  const renderOption = (account: ContractualAccountDTO) => {
    return (
      <div className="d-flex align-items-center">
        <CommodityIconList
          commodityDetails={account.commodityDetail!}
          includeColor
          className={styles.icons}
          isProduction={account.isProduction}
        />
        <div className={styles.textContent}>
          <div className="text-truncate">
            <span>{account.number}</span>
          </div>
          <div className="small text-muted text-truncate mt-n1">
            {getInlineAddress(account.address!)}
          </div>
        </div>
      </div>
    );
  }

  const renderMultiValue = (props: MultiValueProps<ContractualAccountDTO>) => (
    <components.MultiValue {...props}>
      {props.data.number}
    </components.MultiValue>
  );

  const renderSingleValue = (props: SingleValueProps<ContractualAccountDTO>) => (
    <components.SingleValue {...props}>
      {props.data.number}
    </components.SingleValue>
  );

  // render
  return (
    <FormGroup
      labelText={label ?? tLabels("contractualAccountSelect.cardTitle")}
      validationError={errors ? errors[name] : undefined}
    >
      <Select
        isMulti={isMulti}
        hideSelectedOptions={false}
        options={contractualAccounts}
        closeMenuOnSelect={isMulti ? false : true}
        getOptionLabel={(account) => account.number!}
        getOptionValue={(account) => account.number!}
        value={
          !isEmpty(selectedContractualAccount!)
            ? selectedContractualAccount
            : undefined
        }
        onChange={selectChangeHandler}
        formatOptionLabel={(account) => renderOption(account)}
        components={{
          MultiValue: renderMultiValue,
          SingleValue: renderSingleValue,
        }}
        filterOption={createFilter(filterConfig)}
        captureMenuScroll={false}
        className={`${reactSelectStyles.customSelect}`}
        classNamePrefix="customSelect"
        placeholder={getPlaceholderText()}
        noOptionsMessage={() =>
          tLabels("contractualAccountSelect.contractualAccountNotFound")
        }
      />
    </FormGroup>
  )
}

export default ContractualAccountSelect;
